import React, { useState, useEffect } from 'react';
import { evaluationApi, EvaluationStats } from '../services/evaluationApi';
import { statsApi, SessionStats } from '../services/statsApi';
import { MarkdownFeedback } from './MarkdownFeedback';
import '../styles/evaluation.css';

interface SessionStatsProps {
  session: any;
  questions: any[];
  sessionStats?: SessionStats | null;
}

interface StudentStatsProps {
  session: any;
  questions: any[];
  studentId: string;
  studentName: string;
}

// Composant pour les statistiques générales de l'enseignant
export const TeacherSessionStats: React.FC<SessionStatsProps> = ({ session, questions, sessionStats: propSessionStats }) => {
  const [evaluationStats, setEvaluationStats] = useState<{[questionId: string]: EvaluationStats}>({});
  const [evaluatingQuestions, setEvaluatingQuestions] = useState<Set<string>>(new Set());
  const [evaluationStatus, setEvaluationStatus] = useState<{configured: boolean, message: string} | null>(null);
  const [sessionStats, setSessionStats] = useState<SessionStats | null>(null);

  // Debug: Afficher les données reçues
  console.log('🔍 Debug TeacherSessionStats:', {
    session: session,
    questions: questions
  });

  // Charger le statut de configuration du service d'évaluation
  useEffect(() => {
    const loadEvaluationStatus = async () => {
      try {
        const status = await evaluationApi.getEvaluationStatus();
        setEvaluationStatus(status);
      } catch (error) {
        console.error('Erreur lors du chargement du statut d\'évaluation:', error);
        setEvaluationStatus({ configured: false, message: 'Erreur lors du chargement du statut' });
      }
    };

    loadEvaluationStatus();
  }, []);

  // Utiliser les statistiques passées en prop ou les charger
  useEffect(() => {
    if (propSessionStats) {
      setSessionStats(propSessionStats);
    } else {
      const loadSessionStats = async () => {
        try {
          const stats = await statsApi.getSessionStats(session.id);
          setSessionStats(stats.stats);
          console.log('📊 Statistiques de session chargées:', stats.stats);
        } catch (error) {
          console.error('Erreur lors du chargement des statistiques de session:', error);
          // Fallback sur les calculs locaux si l'API n'est pas disponible
          setSessionStats(null);
        }
      };

      if (session?.id) {
        loadSessionStats();
      }
    }
  }, [session?.id, propSessionStats]);

  // Charger les statistiques d'évaluation pour les questions ouvertes
  useEffect(() => {
    const loadEvaluationStats = async () => {
      const openEndedQuestions = questions.filter(q => q.type === 'open_ended');
      console.log('🔍 Debug Open Ended Questions:', openEndedQuestions);
      for (const question of openEndedQuestions) {
        try {
          console.log('🔍 Debug Question ID:', question.id);
          const stats = await evaluationApi.getEvaluationStats(question.id);
          console.log('🔍 Debug Stats:', stats);
          setEvaluationStats(prev => ({
            ...prev,
            [question.id]: stats
          }));
        } catch (error) {
          console.error(`Erreur lors du chargement des stats pour la question ${question.id}:`, error);
        }
      }
    };

    if (questions.length > 0) {
      loadEvaluationStats();
    }
  }, [questions]);

  // Fonction pour évaluer toutes les réponses d'une question ouverte
  const handleEvaluateAllResponses = async (questionId: string) => {
    setEvaluatingQuestions(prev => new Set(prev).add(questionId));
    
    try {
      const result = await evaluationApi.evaluateAllResponses(questionId);
      
      if (result.success) {
        // Recharger les statistiques pour cette question
        const updatedStats = await evaluationApi.getEvaluationStats(questionId);
        setEvaluationStats(prev => ({
          ...prev,
          [questionId]: updatedStats
        }));
        
        alert(`Évaluation terminée: ${result.evaluatedCount} réponses évaluées avec succès${result.errorCount > 0 ? `, ${result.errorCount} erreurs` : ''}`);
      }
    } catch (error) {
      console.error('Erreur lors de l\'évaluation:', error);
      alert(`Erreur lors de l'évaluation: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    } finally {
      setEvaluatingQuestions(prev => {
        const newSet = new Set(prev);
        newSet.delete(questionId);
        return newSet;
      });
    }
  };
  
  // Filtrer les questions qui ont une bonne réponse définie (QCM et Vrai/Faux)
  const questionsWithCorrectAnswer = questions.filter(question => 
    question.correctAnswer !== undefined && 
    question.correctAnswer !== null && 
    question.correctAnswer !== '' &&
    (question.type === 'multiple_choice' || question.type === 'true_false')
  );
  console.log('🔍 Debug Questions with correct answer:', questionsWithCorrectAnswer);
  // Calculer les statistiques générales
  const totalQuestions = questions.length;
  const totalQuestionsWithCorrectAnswer = questionsWithCorrectAnswer.length;
  const totalStudents = session?.participantsCount || 0;
  
  // Calculer le nombre total de réponses uniques (toutes les questions pour la participation)
  const allResponses = questions.flatMap(question => question.responses || []);
  const totalResponses = allResponses.length;
  
  console.log('🔍 Debug Stats générales:', {
    totalQuestions,
    totalStudents,
    totalResponses,
    allResponses
  });
  
  // Calculer les statistiques par question (toutes les questions pour la participation)
  const questionStats = questions.map(question => {
    const responses = question.responses || [];
    const responseCount = responses.length;
    
    // Calculer le taux de participation basé sur les étudiants uniques qui ont répondu
    const uniqueRespondents = new Set(responses.map((r: any) => r.studentId)).size;
    const participationRate = totalStudents > 0 ? (uniqueRespondents / totalStudents) * 100 : 0;
    
    // Calculer la distribution des réponses selon le type de question
    let answerDistribution: { [key: string]: number } = {};
    let totalValidResponses = 0;
    
    if ((question.type === 'multiple_choice' || question.type === 'investigation') && question.options) {
      // Pour les questions à choix multiples
      question.options.forEach((option: any, index: number) => {
        answerDistribution[option.text] = 0;
      });
      
      responses.forEach((response: any) => {
        const responseIndex = parseInt(response.answer);
        
        if (!isNaN(responseIndex) && responseIndex >= 0 && responseIndex < question.options.length) {
          const optionText = question.options[responseIndex].text;
          answerDistribution[optionText]++;
          totalValidResponses++;
        }
      });
      
    } else if (question.type === 'true_false' && question.options) {
      // Pour les questions vrai/faux
      console.log('🔍 Debug True/False - Options:', question.options);
      
      question.options.forEach((option: any, index: number) => {
        // Gérer les options qui peuvent être des chaînes ou des objets
        const optionText = typeof option === 'string' ? option : option.text || option.toString();
        console.log('🔍 Debug True/False - Option:', option, '-> Text:', optionText);
        answerDistribution[optionText] = 0;
      });
      
      responses.forEach((response: any) => {
        const responseIndex = parseInt(response.answer);
        console.log('🔍 Debug True/False - Response:', response.answer, '-> Index:', responseIndex);
        
        if (!isNaN(responseIndex) && responseIndex >= 0 && responseIndex < question.options.length) {
          const option = question.options[responseIndex];
          const optionText = typeof option === 'string' ? option : option.text || option.toString();
          console.log('🔍 Debug True/False - Matched option:', option, '-> Text:', optionText);
          answerDistribution[optionText]++;
          totalValidResponses++;
        }
      });
      
    } else if (question.type === 'rating' && question.ratingScale) {
      // Pour les questions de notation
      const min = question.ratingScale.min || 1;
      const max = question.ratingScale.max || 10;
      
      // Initialiser toutes les notes possibles à 0
      for (let i = min; i <= max; i++) {
        answerDistribution[i.toString()] = 0;
      }
      
      responses.forEach((response: any) => {
        const rating = parseInt(response.answer);
        
        if (!isNaN(rating) && rating >= min && rating <= max) {
          answerDistribution[rating.toString()]++;
          totalValidResponses++;
        }
      });
    } else if (question.type === 'open_ended') {
      // Pour les questions ouvertes, afficher la distribution des scores de concordance
      const evaluatedResponses = responses.filter((r: any) => r.concordanceScore !== null);
      
      if (evaluatedResponses.length > 0) {
        // Catégoriser les scores
        const excellent = evaluatedResponses.filter((r: any) => r.concordanceScore >= 90).length;
        const good = evaluatedResponses.filter((r: any) => r.concordanceScore >= 70 && r.concordanceScore < 90).length;
        const average = evaluatedResponses.filter((r: any) => r.concordanceScore >= 50 && r.concordanceScore < 70).length;
        const poor = evaluatedResponses.filter((r: any) => r.concordanceScore < 50).length;
        
        answerDistribution = {
          'Excellent (90-100%)': excellent,
          'Bon (70-89%)': good,
          'Moyen (50-69%)': average,
          'Insuffisant (0-49%)': poor
        };
        
        totalValidResponses = evaluatedResponses.length;
      } else {
        // Aucune évaluation disponible
        answerDistribution = {
          'Non évaluées': responses.length
        };
        totalValidResponses = responses.length;
      }
    }
    
    // Debug final
    console.log('🔍 Debug Distribution finale:', {
      question: question.title,
      type: question.type,
      answerDistribution,
      totalValidResponses,
      totalResponses: responses.length
    });
    
    return {
      question,
      responseCount,
      participationRate,
      uniqueRespondents,
      answerDistribution,
      totalValidResponses
    };
  });

  // Calculer les statistiques globales
  const averageParticipation = totalQuestions > 0 
    ? questionStats.reduce((sum, stat) => sum + stat.participationRate, 0) / totalQuestions 
    : 0;

  // Calculer les statistiques des questions ouvertes évaluées
  const openEndedQuestions = questions.filter(q => q.type === 'open_ended');
  const evaluatedOpenEndedQuestions = openEndedQuestions.filter(q => 
    q.responses && q.responses.some((r: any) => r.concordanceScore !== null)
  );
  
  let averageOpenEndedScore = 0;
  if (evaluatedOpenEndedQuestions.length > 0) {
    const totalScore = evaluatedOpenEndedQuestions.reduce((sum, question) => {
      const evaluatedResponses = question.responses.filter((r: any) => r.concordanceScore !== null);
      if (evaluatedResponses.length > 0) {
        const questionScore = evaluatedResponses.reduce((s: number, r: any) => s + r.concordanceScore, 0) / evaluatedResponses.length;
        return sum + questionScore;
      }
      return sum;
    }, 0);
    averageOpenEndedScore = totalScore / evaluatedOpenEndedQuestions.length;
  }

  // Calculer le score moyen global (QCM/VF + Questions ouvertes)
  let globalAverageScore = 0;
  let totalEvaluableQuestions = 0;
  
  // Score des QCM/VF
  if (questionsWithCorrectAnswer.length > 0) {
    const qcmScore = questionStats
      .filter(stat => (stat.question.type === 'multiple_choice' || stat.question.type === 'true_false') && 
                     stat.question.correctAnswer !== undefined && 
                     stat.question.correctAnswer !== null && 
                     stat.question.correctAnswer !== '')
      .reduce((sum, stat) => {
        const correctCount = stat.question.responses.filter((r: any) => {
          const studentAnswerIndex = parseInt(r.answer);
          const correctAnswerIndex = parseInt(stat.question.correctAnswer);
          return !isNaN(studentAnswerIndex) && !isNaN(correctAnswerIndex) && studentAnswerIndex === correctAnswerIndex;
        }).length;
        const totalResponses = stat.question.responses.length;
        const questionScore = totalResponses > 0 ? (correctCount / totalResponses) * 100 : 0;
        return sum + questionScore;
      }, 0);
    
    const qcmAverage = qcmScore / questionsWithCorrectAnswer.length;
    globalAverageScore += qcmAverage * questionsWithCorrectAnswer.length;
    totalEvaluableQuestions += questionsWithCorrectAnswer.length;
  }
  
  // Score des questions ouvertes évaluées
  if (evaluatedOpenEndedQuestions.length > 0) {
    globalAverageScore += averageOpenEndedScore * evaluatedOpenEndedQuestions.length;
    totalEvaluableQuestions += evaluatedOpenEndedQuestions.length;
  }
  
  // Calculer la moyenne pondérée globale
  if (totalEvaluableQuestions > 0) {
    globalAverageScore = globalAverageScore / totalEvaluableQuestions;
  }

  return (
    <div className="session-statistics">
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">📊 Statistiques Générales de la Session</h3>
        </div>
        <div className="card-content">
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number">{totalQuestions}</div>
              <div className="stat-label">Questions Total</div>
              <div className="stat-subtitle">Avec bonne réponse: {totalQuestionsWithCorrectAnswer}</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{totalStudents}</div>
              <div className="stat-label">Étudiants</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{sessionStats ? sessionStats.totalResponses : totalResponses}</div>
              <div className="stat-label">Réponses</div>
              <div className="stat-subtitle">
                {sessionStats ? 'Sans doublons' : 'Questions avec bonne réponse uniquement'}
                {sessionStats && sessionStats.totalResponses !== totalResponses && (
                  <span style={{color: '#e74c3c', marginLeft: '8px'}}>
                    (Doublons ignorés: {totalResponses - sessionStats.totalResponses})
                  </span>
                )}
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{averageParticipation.toFixed(2)}%</div>
              <div className="stat-label">Participation Moy.</div>
            </div>
            {totalEvaluableQuestions > 0 && (
              <div className="stat-item">
                <div className="stat-number">
                  {sessionStats ? sessionStats.globalAccuracy.toFixed(1) : globalAverageScore.toFixed(1)}%
                </div>
                <div className="stat-label">Score Moyen Global</div>
                <div className="stat-subtitle">
                  {sessionStats ? (
                    <>
                      {sessionStats.questionsWithCorrectAnswer > 0 && `${sessionStats.questionsWithCorrectAnswer} QCM/VF`}
                      {sessionStats.questionsWithCorrectAnswer > 0 && sessionStats.openEndedQuestions > 0 && ' + '}
                      {sessionStats.openEndedQuestions > 0 && `${sessionStats.openEndedQuestions} ouvertes`}
                      <br />
                      <span style={{color: '#27ae60', fontSize: '0.9em'}}>Sans doublons</span>
                    </>
                  ) : (
                    <>
                      {questionsWithCorrectAnswer.length > 0 && `${questionsWithCorrectAnswer.length} QCM/VF`}
                      {questionsWithCorrectAnswer.length > 0 && evaluatedOpenEndedQuestions.length > 0 && ' + '}
                      {evaluatedOpenEndedQuestions.length > 0 && `${evaluatedOpenEndedQuestions.length} ouvertes`}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Statistiques détaillées par question */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">📈 Détail par Question</h3>
        </div>
        <div className="card-content">
          {questionStats.map((stat, index) => (
            <div key={stat.question.id} className="question-stat">
              <div className="question-stat-header">
                <h4>Question {index + 1}: {stat.question.title}</h4>
                <div className="question-stat-meta">
                  <span className="stat-badge">
                    {stat.responseCount} réponses
                  </span>
                  <span className="stat-badge">
                    {stat.uniqueRespondents} étudiants
                  </span>
                  <span className="stat-badge">
                    {stat.participationRate.toFixed(2)}% participation
                  </span>
                </div>
              </div>
              
              {(stat.question.type === 'multiple_choice' || stat.question.type === 'investigation' || stat.question.type === 'true_false' || stat.question.type === 'rating' || stat.question.type === 'open_ended') && Object.keys(stat.answerDistribution).length > 0 && (
                <div className="answer-distribution">
                  <h5>
                    {stat.question.type === 'multiple_choice' || stat.question.type === 'investigation' ? 'Répartition des réponses :' : 
                     stat.question.type === 'true_false' ? 'Répartition des réponses :' : 
                     stat.question.type === 'rating' ? 'Distribution des notes :' :
                     'Distribution des scores de concordance :'}
                  </h5>
                  <div className="distribution-chart">
                    {Object.entries(stat.answerDistribution).map(([answer, count]) => (
                      <div key={answer} className="distribution-item">
                        <div className="distribution-label">
                          {stat.question.type === 'rating' ? `Note ${answer}` : answer}
                        </div>
                        <div className="distribution-bar">
                          <div 
                            className="distribution-fill"
                            style={{ 
                              width: `${stat.totalValidResponses > 0 ? (count as number / stat.totalValidResponses) * 100 : 0}%`,
                              backgroundColor: stat.question.type === 'rating' ? '#28a745' : 
                                             stat.question.type === 'true_false' ? '#ffc107' : 
                                             stat.question.type === 'open_ended' ? (
                                               answer.includes('Excellent') ? '#28a745' :
                                               answer.includes('Bon') ? '#17a2b8' :
                                               answer.includes('Moyen') ? '#ffc107' :
                                               answer.includes('Insuffisant') ? '#dc3545' :
                                               '#6c757d'
                                             ) : undefined
                            }}
                          ></div>
                        </div>
                        <div className="distribution-count">
                          {count} ({stat.totalValidResponses > 0 ? ((count as number / stat.totalValidResponses) * 100).toFixed(2) : 0}%)
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Afficher les réponses non matchées si il y en a */}
                  {stat.responseCount > stat.totalValidResponses && (
                    <div className="unmatched-responses">
                      <h6>Réponses non classées :</h6>
                      <p><em>{stat.responseCount - stat.totalValidResponses} réponse(s) ne correspondent pas aux options définies</em></p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Section spéciale pour les détails des évaluations des questions ouvertes */}
              {stat.question.type === 'open_ended' && stat.question.responses && stat.question.responses.length > 0 && (
                <div className="open-ended-evaluations">
                  <h5>📝 Détail des Évaluations</h5>
                  <div className="evaluations-list">
                    {stat.question.responses
                      .filter((response: any) => response.concordanceScore !== null)
                      .map((response: any, index: number) => (
                        <div key={index} className="evaluation-item">
                          <div className="evaluation-header">
                            <div className="student-info">
                              <strong>{response.studentName}</strong>
                              <span className={`score-badge ${
                                response.concordanceScore >= 90 ? 'score-excellent' :
                                response.concordanceScore >= 70 ? 'score-good' :
                                response.concordanceScore >= 50 ? 'score-average' : 'score-poor'
                              }`}>
                                {response.concordanceScore}%
                              </span>
                            </div>
                            <div className="evaluation-meta">
                              <small>
                                Évalué le {new Date(response.evaluationTimestamp).toLocaleString()}
                              </small>
                            </div>
                          </div>
                          
                          <div className="evaluation-content">
                            <div className="student-answer">
                              <strong>Réponse de l'étudiant :</strong>
                              <p className="answer-text">{response.answer}</p>
                            </div>
                            
                            {response.aiFeedback && (
                              <div className="ai-feedback">
                                <strong>Feedback automatique :</strong>
                                <div className="feedback-text">
                                  <MarkdownFeedback content={response.aiFeedback} />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    
                    {/* Afficher les réponses non évaluées */}
                    {stat.question.responses.filter((r: any) => r.concordanceScore === null).length > 0 && (
                      <div className="unevaluated-responses">
                        <h6>Réponses non évaluées :</h6>
                        <div className="unevaluated-list">
                          {stat.question.responses
                            .filter((response: any) => response.concordanceScore === null)
                            .map((response: any, index: number) => (
                              <div key={index} className="unevaluated-item">
                                <div className="student-info">
                                  <strong>{response.studentName}</strong>
                                  <span className="status-badge status-created">Non évaluée</span>
                                </div>
                                <div className="student-answer">
                                  <p className="answer-text">{response.answer}</p>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Section d'évaluation automatique pour les questions ouvertes */}
              {stat.question.type === 'open_ended' && (
                <div className="open-ended-evaluation">
                  <h5>🤖 Évaluation Automatique</h5>
                  
                  {/* Statut de configuration */}
                  {/* {evaluationStatus && (
                    <div className={`evaluation-status ${evaluationStatus.configured ? 'status-active' : 'status-created'}`}>
                      <span className="status-badge">
                        {evaluationStatus.configured ? '✅ Service configuré' : '❌ Service non configuré'}
                      </span>
                      <p className="status-message">{evaluationStatus.message}</p>
                    </div>
                  )} */}
                  
                  {/* Statistiques d'évaluation */}
                  {evaluationStats[stat.question.id] && (
                    <div className="evaluation-stats">
                      <div className="stats-summary">
                        <div className="stat-item">
                          <div className="stat-number">{evaluationStats[stat.question.id].evaluatedResponses}</div>
                          <div className="stat-label">Réponses Évaluées</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-number">{evaluationStats[stat.question.id].averageScore.toFixed(1)}%</div>
                          <div className="stat-label">Score Moyen</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-number">{evaluationStats[stat.question.id].evaluationRate}%</div>
                          <div className="stat-label">Taux d'Évaluation</div>
                        </div>
                      </div>
                      
                      {/* Distribution des scores */}
                      {evaluationStats[stat.question.id].evaluatedResponses > 0 && (
                        <div className="score-distribution">
                          <h6>Distribution des Scores :</h6>
                          <div className="distribution-chart">
                            <div className="distribution-item">
                              <div className="distribution-label">Excellent (90-100%)</div>
                              <div className="distribution-bar">
                                <div 
                                  className="distribution-fill"
                                  style={{ 
                                    width: `${(evaluationStats[stat.question.id].scoreDistribution.excellent / evaluationStats[stat.question.id].evaluatedResponses) * 100}%`,
                                    backgroundColor: '#28a745'
                                  }}
                                ></div>
                              </div>
                              <div className="distribution-count">
                                {evaluationStats[stat.question.id].scoreDistribution.excellent}
                              </div>
                            </div>
                            <div className="distribution-item">
                              <div className="distribution-label">Bon (70-89%)</div>
                              <div className="distribution-bar">
                                <div 
                                  className="distribution-fill"
                                  style={{ 
                                    width: `${(evaluationStats[stat.question.id].scoreDistribution.good / evaluationStats[stat.question.id].evaluatedResponses) * 100}%`,
                                    backgroundColor: '#17a2b8'
                                  }}
                                ></div>
                              </div>
                              <div className="distribution-count">
                                {evaluationStats[stat.question.id].scoreDistribution.good}
                              </div>
                            </div>
                            <div className="distribution-item">
                              <div className="distribution-label">Moyen (50-69%)</div>
                              <div className="distribution-bar">
                                <div 
                                  className="distribution-fill"
                                  style={{ 
                                    width: `${(evaluationStats[stat.question.id].scoreDistribution.average / evaluationStats[stat.question.id].evaluatedResponses) * 100}%`,
                                    backgroundColor: '#ffc107'
                                  }}
                                ></div>
                              </div>
                              <div className="distribution-count">
                                {evaluationStats[stat.question.id].scoreDistribution.average}
                              </div>
                            </div>
                            <div className="distribution-item">
                              <div className="distribution-label">Insuffisant (0-49%)</div>
                              <div className="distribution-bar">
                                <div 
                                  className="distribution-fill"
                                  style={{ 
                                    width: `${(evaluationStats[stat.question.id].scoreDistribution.poor / evaluationStats[stat.question.id].evaluatedResponses) * 100}%`,
                                    backgroundColor: '#dc3545'
                                  }}
                                ></div>
                              </div>
                              <div className="distribution-count">
                                {evaluationStats[stat.question.id].scoreDistribution.poor}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Bouton d'évaluation */}
                      {evaluationStatus?.configured && evaluationStats[stat.question.id].unevaluatedResponses > 0 && (
                        <div className="evaluation-actions">
                          <button 
                            className="btn btn-primary btn-sm"
                            onClick={() => handleEvaluateAllResponses(stat.question.id)}
                            disabled={evaluatingQuestions.has(stat.question.id)}
                          >
                            {evaluatingQuestions.has(stat.question.id) ? (
                              <>⏳ Évaluation en cours...</>
                            ) : (
                              <>🤖 Évaluer {evaluationStats[stat.question.id].unevaluatedResponses} réponse(s) restante(s)</>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Composant pour les statistiques personnelles de l'étudiant
export const StudentSessionStats: React.FC<StudentStatsProps> = ({ session, questions, studentId, studentName }) => {
  // Calculer les statistiques personnelles
  const totalQuestions = questions.length;
  const studentResponses = questions.filter(question => 
    question.responses?.some((response: any) => response.studentId === studentId)
  );
  const answeredQuestions = studentResponses.length;
  const participationRate = totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;
  
  // Calculer les bonnes réponses pour les questions à choix multiples et vrai/faux
  const questionsWithCorrectAnswer = studentResponses.filter(question => 
    (question.type === 'multiple_choice' || question.type === 'true_false' || question.type === 'investigation') && 
    question.correctAnswer !== undefined && 
    question.correctAnswer !== null
  );
  
  const correctAnswers = questionsWithCorrectAnswer.filter(question => {
    const studentResponse = question.responses.find((r: any) => r.studentId === studentId);
    if (!studentResponse) return false;
    
    // Convertir la réponse de l'étudiant en index
    const studentAnswerIndex = parseInt(studentResponse.answer);
    const correctAnswerIndex = parseInt(question.correctAnswer);
    
    return !isNaN(studentAnswerIndex) && !isNaN(correctAnswerIndex) && studentAnswerIndex === correctAnswerIndex;
  }).length;

  // Calculer les scores pour les questions ouvertes évaluées
  const openEndedQuestions = studentResponses.filter(question => 
    question.type === 'open_ended'
  );
  
  const evaluatedOpenEndedQuestions = openEndedQuestions.filter(question => {
    const studentResponse = question.responses.find((r: any) => r.studentId === studentId);
    return studentResponse && studentResponse.concordanceScore !== null;
  });
  
  // Calculer le score moyen des questions ouvertes évaluées
  let openEndedScore = 0;
  if (evaluatedOpenEndedQuestions.length > 0) {
    const totalScore = evaluatedOpenEndedQuestions.reduce((sum, question) => {
      const studentResponse = question.responses.find((r: any) => r.studentId === studentId);
      return sum + (studentResponse?.concordanceScore || 0);
    }, 0);
    openEndedScore = totalScore / evaluatedOpenEndedQuestions.length;
  }

  // Calculer la précision globale (QCM/VF + Questions ouvertes)
  const totalEvaluableQuestions = questionsWithCorrectAnswer.length + evaluatedOpenEndedQuestions.length;
  let globalAccuracyRate = 0;
  
  if (totalEvaluableQuestions > 0) {
    // Score des QCM/VF (0 ou 100%)
    const qcmScore = questionsWithCorrectAnswer.length > 0 ? (correctAnswers / questionsWithCorrectAnswer.length) * 100 : 0;
    
    // Score des questions ouvertes (score de concordance moyen)
    const openEndedContribution = evaluatedOpenEndedQuestions.length > 0 ? openEndedScore : 0;
    
    // Calculer la moyenne pondérée
    const qcmWeight = questionsWithCorrectAnswer.length / totalEvaluableQuestions;
    const openEndedWeight = evaluatedOpenEndedQuestions.length / totalEvaluableQuestions;
    
    globalAccuracyRate = (qcmScore * qcmWeight) + (openEndedContribution * openEndedWeight);
  }

  // Ancien calcul de précision (QCM/VF seulement) pour compatibilité
  const accuracyRate = questionsWithCorrectAnswer.length > 0 ? (correctAnswers / questionsWithCorrectAnswer.length) * 100 : 0;

  // Fonction pour imprimer les résultats (sans header ni bouton)
  const exportResultsToPDF = () => {
    try {
      // Vérifier que les données sont disponibles
      if (!questions || questions.length === 0) {
        alert('Aucune question disponible pour l\'export');
        return;
      }

      if (!session) {
        alert('Session non disponible pour l\'export');
        return;
      }

      console.log('Début de l\'impression avec les données:', {
        questions: questions.length,
        session: session.name,
        studentId: studentId,
        studentName: studentName
      });

      // Masquer le header et le bouton de capture
      const header = document.querySelector('header, .header, .navbar, .nav-header');
      const captureButton = document.getElementById('print-button');
      
      if (header) {
        (header as HTMLElement).style.display = 'none';
      }
      if (captureButton) {
        (captureButton as HTMLElement).style.display = 'none';
      }

      // Attendre un court délai pour que le DOM se mette à jour
      setTimeout(() => {
        // Lancer l'impression
        window.print();

        // Restaurer les éléments après l'impression
        setTimeout(() => {
          if (header) {
            (header as HTMLElement).style.display = '';
          }
          if (captureButton) {
            (captureButton as HTMLElement).style.display = '';
          }
        }, 1000);
      }, 100);

    } catch (error) {
      console.error('Erreur lors de l\'impression:', error);
      alert(`Erreur lors de l'impression: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    }
  };

  return (
    <div className="student-statistics">
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">📊 Vos Statistiques Personnelles</h3>
          <button 
            id="print-button"
            onClick={exportResultsToPDF}
            className="btn btn-outline btn-sm"
            style={{ marginLeft: 'auto' }}
          >
            🖨️ Imprimer
          </button>
        </div>
        <div className="card-content">
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number">{answeredQuestions}</div>
              <div className="stat-label">Questions Répondues</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{totalQuestions}</div>
              <div className="stat-label">Questions Total</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{participationRate.toFixed(2)}%</div>
              <div className="stat-label">Participation</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{globalAccuracyRate.toFixed(2)}%</div>
              <div className="stat-label">Précision Globale</div>
              <div className="stat-subtitle">
                QCM/VF: {accuracyRate.toFixed(1)}% | 
                Questions ouvertes: {evaluatedOpenEndedQuestions.length > 0 ? openEndedScore.toFixed(1) + '%' : 'Non évaluées'}
              </div>
            </div>
            {/* {totalEvaluableQuestions > 0 && (
              <div className="stat-item">
                <div className="stat-number">
                  {questionsWithCorrectAnswer.length > 0 ? correctAnswers : 0}
                  {evaluatedOpenEndedQuestions.length > 0 ? ` + ${evaluatedOpenEndedQuestions.length} évaluées` : ''}
                  /{totalEvaluableQuestions}
                </div>
                <div className="stat-label">Questions Évaluées</div>
                <div className="stat-subtitle">
                  {questionsWithCorrectAnswer.length > 0 && `${questionsWithCorrectAnswer.length} QCM/VF`}
                  {questionsWithCorrectAnswer.length > 0 && evaluatedOpenEndedQuestions.length > 0 && ' + '}
                  {evaluatedOpenEndedQuestions.length > 0 && `${evaluatedOpenEndedQuestions.length} ouvertes`}
                </div>
              </div>
            )} */}
          </div>
        </div>
      </div>

      {/* Détail des réponses personnelles */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">📝 Vos Réponses</h3>
        </div>
        <div className="card-content">
          {questions.map((question, index) => {
            const studentResponse = question.responses?.find((r: any) => r.studentId === studentId);
            
            // Pour les questions à choix multiples et vrai/faux, comparer les index
            let isCorrect = null;
            let openEndedScore = null;
            
            if ((question.type === 'multiple_choice' || question.type === 'true_false') && question.correctAnswer !== undefined && question.correctAnswer !== null && studentResponse) {
              const studentAnswerIndex = parseInt(studentResponse.answer);
              const correctAnswerIndex = parseInt(question.correctAnswer);
              isCorrect = !isNaN(studentAnswerIndex) && !isNaN(correctAnswerIndex) && studentAnswerIndex === correctAnswerIndex;
            }
            
            // Pour les questions ouvertes, récupérer le score de concordance
            if (question.type === 'open_ended' && studentResponse && studentResponse.concordanceScore !== null) {
              openEndedScore = studentResponse.concordanceScore;
            }
            
            return (
              <div key={question.id} className="personal-response">
                <div className="response-header">
                  <h4>Question {index + 1}: {question.title}</h4>
                  <div className="response-status">
                    {studentResponse ? (
                      <span className={`status-badge ${
                        isCorrect === true ? 'status-active' : 
                        isCorrect === false ? 'status-created' : 
                        openEndedScore !== null ? (
                          openEndedScore >= 70 ? 'status-active' : 
                          openEndedScore >= 50 ? 'status-info' : 'status-created'
                        ) : 'status-info'
                      }`}>
                        {isCorrect === true ? '✅ Correct' : 
                         isCorrect === false ? '❌ Incorrect' : 
                         openEndedScore !== null ? `🤖 ${openEndedScore.toFixed(0)}% concordance` : 
                         '📝 Répondu'}
                      </span>
                    ) : (
                      <span className="status-badge status-created">
                        ⏭️ Non répondu
                      </span>
                    )}
                  </div>
                </div>
                
                {studentResponse && (
                  <div className="response-details">
                    <p><strong>Votre réponse :</strong> {
                      (question.type === 'multiple_choice' || question.type === 'true_false' || question.type === 'rating' || question.type === 'investigation') && question.options 
                        ? (() => {
                            const answerIndex = parseInt(studentResponse.answer);
                            return !isNaN(answerIndex) && answerIndex >= 0 && answerIndex < question.options.length
                              ? question.options[answerIndex].text
                              : `Index invalide: ${studentResponse.answer}`;
                          })()
                        : studentResponse.answer
                    }</p>
                    {(question.type === 'multiple_choice' || question.type === 'true_false') && question.correctAnswer !== undefined && question.correctAnswer !== null ? (
                      <p><strong>Bonne réponse :</strong> {
                        question.options && question.options[parseInt(question.correctAnswer)]
                          ? question.options[parseInt(question.correctAnswer)].text
                          : `Index invalide: ${question.correctAnswer}`
                      }</p>
                    ) : (question.type === 'multiple_choice' || question.type === 'true_false' || question.type === 'investigation') && (
                      <p><em>Cette question n'a pas de bonne réponse définie</em></p>
                    )}
                    <p className="response-time">
                      Répondu le {new Date(studentResponse.timestamp).toLocaleString()}
                    </p>
                    {studentResponse.modificationCount > 0 && (
                      <p className="modification-info">
                        <em>Modifié {studentResponse.modificationCount} fois</em>
                      </p>
                    )}
                    
                    {/* Afficher l'explication pour les questions à choix multiples */}
                    {(question.type === 'multiple_choice' || question.type === 'true_false') && question.options && studentResponse && (() => {
                      const answerIndex = parseInt(studentResponse.answer);
                      const selectedOption = !isNaN(answerIndex) && answerIndex >= 0 && answerIndex < question.options.length
                        ? question.options[answerIndex]
                        : null;
                      
                      return selectedOption && selectedOption.explication ? (
                        <div className="explanation-section">
                          <p><strong>Explication :</strong></p>
                          <div className="explanation-text">
                            <MarkdownFeedback content={selectedOption.explication} />
                          </div>
                        </div>
                      ) : null;
                    })()}
                    
                    {/* Afficher la proposition de réponse pour les questions ouvertes */}
                    {question.type === 'open_ended' && question.proposition && (
                      <div className="suggested-answer-section">
                        <p><strong>Proposition de réponse :</strong></p>
                        <div className="suggested-answer-text">
                          <MarkdownFeedback content={question.proposition} />
                        </div>
                      </div>
                    )}
                    
                    {/* Afficher le score de concordance pour les questions ouvertes */}
                    {question.type === 'open_ended' && studentResponse && studentResponse.concordanceScore !== null && (
                      <div className="concordance-evaluation">
                        <p><strong>Score de concordance :</strong></p>
                        <div className="score-display">
                          <span className={`score-badge ${studentResponse.concordanceScore >= 90 ? 'score-excellent' : 
                                                      studentResponse.concordanceScore >= 70 ? 'score-good' : 
                                                      studentResponse.concordanceScore >= 50 ? 'score-average' : 'score-poor'}`}>
                            {studentResponse.concordanceScore}%
                          </span>
                          <span className="score-label">
                            {studentResponse.concordanceScore >= 90 ? 'Excellent' : 
                             studentResponse.concordanceScore >= 70 ? 'Bon' : 
                             studentResponse.concordanceScore >= 50 ? 'Moyen' : 'Insuffisant'}
                          </span>
                        </div>
                        
                        {studentResponse.aiFeedback && (
                          <div className="ai-feedback">
                            <p><strong>Feedback automatique :</strong></p>
                            <div className="feedback-text">
                              <MarkdownFeedback content={studentResponse.aiFeedback} />
                            </div>
                          </div>
                        )}
                        
                        {studentResponse.evaluationTimestamp && (
                          <p className="evaluation-time">
                            <em>Évalué automatiquement le {new Date(studentResponse.evaluationTimestamp).toLocaleString()}</em>
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
